import { useCallback, useEffect, useMemo, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import {
  attachMatchResultToLeagueFixture,
  createMatchResult,
  deleteMatchResult,
  getCourts,
  getMatchResults,
  searchRegisteredPlayers,
  updateMatchResult,
  voidMatchResult,
} from "../apicalls/matchResults";
import { getLeagues } from "../apicalls/leagues";
import "./style/MatchResults.css";

const emptySet = () => ({ kind: "set", you: "", opp: "" });

const defaultForm = () => ({
  id: null,
  player_a: "",
  player_b: "",
  venue_id: "",
  played_at: new Date().toISOString().slice(0, 10),
  format: "bo3",
  retired: false,
  retiredWinner: "you",
  status: "confirmed",
  sets: [emptySet(), emptySet()],
});

const parseSets = (sets) => {
  if (Array.isArray(sets)) return sets;
  if (typeof sets === "string") {
    try {
      const parsed = JSON.parse(sets);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toDateInput = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const normalizeRows = (rows) => Array.isArray(rows) ? rows : [];

const normalizeSearch = (value) => String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

export const matchResultIdLabel = (row) => {
  const parts = [`Result #${row.id}`];
  if (row.context === "league" || row.league_id) {
    if (row.league_id) parts.push(`League #${row.league_id}`);
    if (row.league_match_id) parts.push(`Fixture #${row.league_match_id}`);
  }
  return parts.join(" · ");
};

export const canAttachLeagueFixtureCorrection = (row) => (
  Boolean(
    row &&
    row.status !== "voided" &&
    row.context !== "league" &&
    row.league_id &&
    row.league_match_id
  )
);

export const filterMatchResultRows = (rows, filters = {}) => {
  const leagueId = String(filters.league_id || "").trim();
  const searchTerms = normalizeSearch(filters.search).split(" ").filter(Boolean);

  return normalizeRows(rows).filter((row) => {
    if (leagueId && String(row.league_id || "") !== leagueId) return false;
    if (!searchTerms.length) return true;

    const haystack = normalizeSearch([
      row.id,
      row.context,
      row.league_id,
      row.league_match_id,
      row.player_a,
      row.player_a_name,
      row.player_b,
      row.player_b_name,
      row.score,
      row.status,
      row.court_name,
      row.venue_id,
    ].filter((value) => value !== undefined && value !== null).join(" "));

    return searchTerms.every((term) => haystack.includes(term));
  });
};

export const leagueFilterLabel = (league) => (
  `${league.name || `League ${league.id}`} (#${league.id})`
);

export const buildLeagueFilterOptions = (leagues, rows) => {
  const map = new Map();
  normalizeRows(leagues).forEach((league) => {
    if (league.id) map.set(String(league.id), league);
  });
  normalizeRows(rows).forEach((row) => {
    if (row.league_id && !map.has(String(row.league_id))) {
      map.set(String(row.league_id), {
        id: row.league_id,
        name: row.league_name || `League ${row.league_id}`,
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => (
    leagueFilterLabel(a).localeCompare(leagueFilterLabel(b), undefined, { numeric: true })
  ));
};

const scoreString = (form) => {
  if (form.retired) return "Retired";
  return form.sets
    .filter((set) => set.you !== "" && set.opp !== "")
    .map((set) => (set.kind === "mtb" ? `[${set.you}-${set.opp}]` : `${set.you}-${set.opp}`))
    .join("  ");
};

const playerLabel = (player) => (
  player.full_name || player.name || player.email || `Player ${player.user_id || player.id}`
);

const playerId = (player) => String(player.user_id || player.id);

function MatchResults() {
  const auth = isAuthenticated();
  const token = auth?.access_token;
  const [rows, setRows] = useState([]);
  const [players, setPlayers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    player_id: "",
    venue_id: "",
    date_from: "",
    date_to: "",
  });
  const [form, setForm] = useState(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  const [resultFilters, setResultFilters] = useState({
    league_id: "",
    search: "",
  });

  const loadResults = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await getMatchResults(filters, token);
      setRows(normalizeRows(data.match_results));
    } catch (err) {
      setError(err.message || "Failed to load match results");
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  const loadPlayers = useCallback(async () => {
    if (!token) return;
    try {
      const data = await searchRegisteredPlayers({ search: playerSearch, perPage: 75 }, token);
      setPlayers(normalizeRows(data.players));
    } catch (err) {
      setError(err.message || "Failed to load players");
    }
  }, [playerSearch, token]);

  useEffect(() => {
    if (!token) return;
    getCourts(token)
      .then((data) => setCourts(normalizeRows(data.courts)))
      .catch((err) => setError(err.message || "Failed to load courts"));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    getLeagues({}, token)
      .then((data) => setLeagues(normalizeRows(data.leagues)))
      .catch((err) => setError(err.message || "Failed to load leagues"));
  }, [token]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    const timer = setTimeout(loadPlayers, 250);
    return () => clearTimeout(timer);
  }, [loadPlayers]);

  const playerOptions = useMemo(() => {
    const map = new Map();
    players.forEach((player) => map.set(playerId(player), player));
    rows.forEach((row) => {
      if (row.player_a && !map.has(String(row.player_a))) {
        map.set(String(row.player_a), { user_id: row.player_a, full_name: row.player_a_name });
      }
      if (row.player_b && !map.has(String(row.player_b))) {
        map.set(String(row.player_b), { user_id: row.player_b, full_name: row.player_b_name });
      }
    });
    return Array.from(map.values());
  }, [players, rows]);

  const leagueOptions = useMemo(() => buildLeagueFilterOptions(leagues, rows), [leagues, rows]);

  const visibleRows = useMemo(() => filterMatchResultRows(rows, resultFilters), [rows, resultFilters]);

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const editRow = (row) => {
    const sets = parseSets(row.sets).map((set) => ({
      kind: set.kind || "set",
      you: set.you ?? "",
      opp: set.opp ?? "",
    }));

    setForm({
      id: row.id,
      player_a: String(row.player_a || ""),
      player_b: String(row.player_b || ""),
      venue_id: String(row.venue_id || ""),
      played_at: toDateInput(row.played_at),
      format: row.format || "bo3",
      retired: Boolean(row.retired),
      retiredWinner: row.retired_winner_side || "you",
      status: row.status === "disputed" || row.status === "pending" ? "confirmed" : (row.status || "confirmed"),
      sets: sets.length ? sets : [emptySet(), emptySet()],
    });
    setIsEditing(true);
    setMessage(row.status === "disputed" ? "Resolve the disputed result, then save as confirmed." : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startNew = ({ clearMessage = true } = {}) => {
    setForm(defaultForm());
    setIsEditing(false);
    if (clearMessage) setMessage("");
    setError("");
  };

  const setScoreCell = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      sets: prev.sets.map((set, i) => (i === index ? { ...set, [key]: value } : set)),
    }));
  };

  const addSet = () => {
    setForm((prev) => ({
      ...prev,
      sets: prev.sets.length >= 3 ? prev.sets : [...prev.sets, emptySet()],
    }));
  };

  const removeSet = (index) => {
    setForm((prev) => ({
      ...prev,
      sets: prev.sets.length <= 1 ? prev.sets : prev.sets.filter((_, i) => i !== index),
    }));
  };

  const buildPayload = () => ({
    reported_by: Number(form.player_a),
    player_a: Number(form.player_a),
    player_b: Number(form.player_b),
    venue_id: Number(form.venue_id),
    played_at: form.played_at,
    format: form.format,
    retired: form.retired ? { winner: form.retiredWinner } : false,
    sets: form.retired ? undefined : form.sets
      .filter((set) => set.you !== "" && set.opp !== "")
      .map((set) => ({
        kind: set.kind,
        you: Number(set.you),
        opp: Number(set.opp),
      })),
    score_string: scoreString(form),
    status: form.status || "confirmed",
  });

  const submit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = buildPayload();
      if (isEditing && form.id) {
        await updateMatchResult(form.id, payload, token);
        setMessage("Match result updated. Ratings replay triggered.");
      } else {
        await createMatchResult(payload, token);
        setMessage("Match result created. Ratings replay triggered.");
      }
      startNew({ clearMessage: false });
      await loadResults();
    } catch (err) {
      setError(err.message || "Failed to save match result");
    } finally {
      setSaving(false);
    }
  };

  const voidRow = async (row) => {
    if (!token) return;
    const reason = window.prompt("Void reason", "Admin correction");
    if (reason === null) return;
    setSaving(true);
    setError("");
    try {
      await voidMatchResult(row.id, reason, token);
      setMessage("Match result voided. League fixtures reopen after the API fix is deployed; ratings replay triggered.");
      await loadResults();
    } catch (err) {
      setError(err.message || "Failed to void match result");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!token) return;
    const ok = window.confirm("Permanently remove this match result and its audit trail? Prefer Void for score corrections.");
    if (!ok) return;
    setSaving(true);
    setError("");
    try {
      await deleteMatchResult(row.id, token);
      setMessage("Match result removed permanently. Ratings replay triggered.");
      if (isEditing && Number(form.id) === Number(row.id)) {
        startNew({ clearMessage: false });
      }
      await loadResults();
    } catch (err) {
      setError(err.message || "Failed to remove match result");
    } finally {
      setSaving(false);
    }
  };

  const attachLeagueFixture = async (row) => {
    if (!token) return;
    const ok = window.confirm(
      `Attach Result #${row.id} to League #${row.league_id} Fixture #${row.league_match_id}?`
    );
    if (!ok) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await attachMatchResultToLeagueFixture(row.id, {
        league_id: Number(row.league_id),
        league_match_id: Number(row.league_match_id),
      }, token);
      setMessage(`Result #${row.id} attached to League #${row.league_id} Fixture #${row.league_match_id}. Ratings replay triggered.`);
      await loadResults();
    } catch (err) {
      setError(err.message || "Failed to attach result to league fixture");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="match-results-page">
      <div className="coaches_title match-results-header">
        <div>
          <h1>Match Results</h1>
          <p>Find league scores, void corrections, resolve disputes, and trigger rating updates.</p>
        </div>
        <button className="coaches_button match-results-new" onClick={startNew}>
          New Result +
        </button>
      </div>

      {message ? <div className="match-results-alert success">{message}</div> : null}
      {error ? <div className="match-results-alert error">{error}</div> : null}

      <section className="match-results-card">
        <h2>{isEditing ? "Edit / Resolve Result" : "Create Score Report"}</h2>
        <form onSubmit={submit} className="match-results-form">
          <div className="match-results-grid">
            <label>
              Player A / Reporter
              <select value={form.player_a} onChange={(e) => updateForm("player_a", e.target.value)} required>
                <option value="">Select player</option>
                {playerOptions.map((player) => (
                  <option key={playerId(player)} value={playerId(player)}>{playerLabel(player)}</option>
                ))}
              </select>
            </label>
            <label>
              Opponent
              <select value={form.player_b} onChange={(e) => updateForm("player_b", e.target.value)} required>
                <option value="">Select player</option>
                {playerOptions.map((player) => (
                  <option key={playerId(player)} value={playerId(player)}>{playerLabel(player)}</option>
                ))}
              </select>
            </label>
            <label>
              Venue
              <select value={form.venue_id} onChange={(e) => updateForm("venue_id", e.target.value)} required>
                <option value="">Select venue</option>
                {courts.map((court) => (
                  <option key={court.id} value={court.id}>{court.name} {court.area ? `(${court.area})` : ""}</option>
                ))}
              </select>
            </label>
            <label>
              Date played
              <input type="date" value={form.played_at} onChange={(e) => updateForm("played_at", e.target.value)} required />
            </label>
            <label>
              Format
              <select value={form.format} onChange={(e) => updateForm("format", e.target.value)}>
                <option value="bo3">Best of 3</option>
                <option value="single">Single set</option>
              </select>
            </label>
            <label>
              Save status
              <select value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                <option value="confirmed">Confirmed / resolved</option>
                <option value="disputed">Disputed</option>
              </select>
            </label>
          </div>

          <label className="match-results-check">
            <input type="checkbox" checked={form.retired} onChange={(e) => updateForm("retired", e.target.checked)} />
            Match did not finish
          </label>

          {form.retired ? (
            <label className="match-results-retired">
              Pick who won
              <select value={form.retiredWinner} onChange={(e) => updateForm("retiredWinner", e.target.value)}>
                <option value="you">Player A</option>
                <option value="opp">Opponent</option>
              </select>
            </label>
          ) : (
            <div className="match-results-scorebox">
              <div className="match-results-score-head">
                <strong>Scores</strong>
                <button type="button" onClick={addSet} disabled={form.sets.length >= 3}>Add set</button>
              </div>
              {form.sets.map((set, index) => (
                <div className="match-results-set" key={index}>
                  <span>Set {index + 1}</span>
                  <select value={set.kind} onChange={(e) => setScoreCell(index, "kind", e.target.value)}>
                    <option value="set">Set</option>
                    <option value="mtb">Match TB</option>
                  </select>
                  <input type="number" min="0" value={set.you} onChange={(e) => setScoreCell(index, "you", e.target.value)} placeholder="Player A" required />
                  <input type="number" min="0" value={set.opp} onChange={(e) => setScoreCell(index, "opp", e.target.value)} placeholder="Opponent" required />
                  <button type="button" onClick={() => removeSet(index)} disabled={form.sets.length <= 1}>Remove</button>
                </div>
              ))}
              <small>Preview: {scoreString(form) || "Enter scores"}</small>
            </div>
          )}

          <div className="match-results-actions">
            <input
              type="text"
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              placeholder="Search players for selects"
            />
            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : isEditing ? "Save Correction" : "Create Result"}
            </button>
          </div>
        </form>
      </section>

      <section className="match-results-card">
        <div className="match-results-correction-note">
          <strong>League correction workflow:</strong> use Attach when a league fixture was logged as recreational.
          Use Void when the score is wrong and should reopen the fixture. Use Remove only when no audit trail should remain.
        </div>
        <div className="match-results-filters">
          <select
            value={resultFilters.league_id}
            onChange={(e) => setResultFilters((prev) => ({ ...prev, league_id: e.target.value }))}
          >
            <option value="">All leagues</option>
            {leagueOptions.map((league) => (
              <option key={league.id} value={league.id}>{leagueFilterLabel(league)}</option>
            ))}
          </select>
          <input
            type="search"
            value={resultFilters.search}
            onChange={(e) => setResultFilters((prev) => ({ ...prev, search: e.target.value }))}
            placeholder="Search IDs, players, score"
          />
          <select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
            <option value="">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="disputed">Disputed</option>
            <option value="voided">Voided</option>
          </select>
          <select value={filters.player_id} onChange={(e) => setFilters((prev) => ({ ...prev, player_id: e.target.value }))}>
            <option value="">All players</option>
            {playerOptions.map((player) => (
              <option key={playerId(player)} value={playerId(player)}>{playerLabel(player)}</option>
            ))}
          </select>
          <select value={filters.venue_id} onChange={(e) => setFilters((prev) => ({ ...prev, venue_id: e.target.value }))}>
            <option value="">All venues</option>
            {courts.map((court) => (
              <option key={court.id} value={court.id}>{court.name}</option>
            ))}
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))} />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))} />
        </div>

        <div className="table-responsive">
          <table className="table match-results-table">
            <thead>
              <tr>
                <th>IDs</th>
                <th>Date</th>
                <th>Players</th>
                <th>Venue</th>
                <th>Score</th>
                <th>Winner</th>
                <th>Status</th>
                <th>Rating</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9">Loading match results...</td></tr>
              ) : visibleRows.length ? visibleRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="match-results-ids">{matchResultIdLabel(row)}</div>
                    <small>{row.context || "recreational"}</small>
                  </td>
                  <td>{toDateInput(row.played_at)}</td>
                  <td>{row.player_a_name || row.player_a} vs {row.player_b_name || row.player_b}</td>
                  <td>{row.court_name || row.venue_id}</td>
                  <td>{row.score}</td>
                  <td>{Number(row.winner) === Number(row.player_a) ? (row.player_a_name || "Player A") : (row.player_b_name || "Opponent")}</td>
                  <td><span className={`match-status ${row.status}`}>{row.status}</span></td>
                  <td>
                    {row.rating_a_delta || row.rating_b_delta
                      ? `${Number(row.rating_a_delta || 0).toFixed(3)} / ${Number(row.rating_b_delta || 0).toFixed(3)}`
                      : "—"}
                  </td>
                  <td>
                    <button className="match-results-link" onClick={() => editRow(row)}>
                      {row.status === "disputed" ? "Resolve" : "Edit"}
                    </button>
                    {row.status !== "voided" ? (
                      <button className="match-results-link danger" onClick={() => voidRow(row)}>Void</button>
                    ) : null}
                    {canAttachLeagueFixtureCorrection(row) ? (
                      <button className="match-results-link" onClick={() => attachLeagueFixture(row)}>Attach</button>
                    ) : null}
                    <button className="match-results-link danger" onClick={() => deleteRow(row)}>Remove</button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="9">No match results found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

export default MatchResults;
